import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../lib/api';
import {
  Settings as SettingsIcon,
  Mail,
  Shield,
  Database,
  Save,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
} from 'lucide-react';

interface SettingsSection {
  id: string;
  name: string;
  icon: React.ReactNode;
  component: React.ComponentType;
}

const SystemSettings: React.FC = () => {
  const [settings, setSettings] = useState({
    siteName: 'Auth-as-a-Service',
    siteUrl: 'https://auth.example.com',
    allowRegistration: true,
    requireEmailVerification: true,
    sessionTimeout: 3600,
    maxLoginAttempts: 5,
    lockoutDuration: 900,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data: loaded } = useQuery({ queryKey: ['system-settings'], queryFn: () => adminAPI.getSystemSettings() });

  // Seed the form from the loaded settings once they arrive.
  useEffect(() => {
    if (loaded) setSettings((prev) => ({ ...prev, ...loaded }));
  }, [loaded]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaved(false);
    try {
      await adminAPI.updateSystemSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">System Configuration</h3>
        <p className="text-sm text-gray-500">General system settings and configuration</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Site Name
          </label>
          <input
            type="text"
            value={settings.siteName}
            onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Site URL
          </label>
          <input
            type="url"
            value={settings.siteUrl}
            onChange={(e) => setSettings({ ...settings, siteUrl: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="allowRegistration"
            checked={settings.allowRegistration}
            onChange={(e) => setSettings({ ...settings, allowRegistration: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="allowRegistration" className="ml-2 text-sm text-gray-700">
            Allow user registration
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="requireEmailVerification"
            checked={settings.requireEmailVerification}
            onChange={(e) => setSettings({ ...settings, requireEmailVerification: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="requireEmailVerification" className="ml-2 text-sm text-gray-700">
            Require email verification for new accounts
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Session Timeout (seconds)
          </label>
          <input
            type="number"
            value={settings.sessionTimeout}
            onChange={(e) => setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Max Login Attempts
          </label>
          <input
            type="number"
            value={settings.maxLoginAttempts}
            onChange={(e) => setSettings({ ...settings, maxLoginAttempts: parseInt(e.target.value) })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Lockout Duration (seconds)
          </label>
          <input
            type="number"
            value={settings.lockoutDuration}
            onChange={(e) => setSettings({ ...settings, lockoutDuration: parseInt(e.target.value) })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-green-600 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />Saved</span>}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

const EmailSettings: React.FC = () => {
  const [settings, setSettings] = useState({
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: 'Auth Service',
    enableTLS: true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isTestingSMTP, setIsTestingSMTP] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: loaded } = useQuery({ queryKey: ['email-settings'], queryFn: () => adminAPI.getEmailSettings() });

  useEffect(() => {
    if (loaded) {
      setSettings((prev) => ({
        ...prev,
        smtpHost: loaded.host || '',
        smtpPort: loaded.port || 587,
        smtpUsername: loaded.user || '',
        fromEmail: loaded.from || '',
      }));
    }
  }, [loaded]);

  const handleSaveSMTP = async () => {
    setIsSaving(true);
    setSaved(false);
    try {
      await adminAPI.updateEmailSettings({
        host: settings.smtpHost,
        port: settings.smtpPort,
        user: settings.smtpUsername,
        pass: settings.smtpPassword || undefined, // blank = leave unchanged
        from: settings.fromEmail,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Failed to save SMTP settings', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestSMTP = async () => {
    const to = window.prompt('Send a test email to which address?');
    if (!to) return;
    setIsTestingSMTP(true);
    setTestResult(null);
    try {
      await adminAPI.testEmail(to);
      setTestResult({ success: true, message: `Test email sent to ${to}.` });
    } catch (error: any) {
      const msg = String(error?.message || '');
      setTestResult({
        success: false,
        message: /not_configured|503/.test(msg)
          ? 'SMTP is not configured — transactional mail falls back to SES. Save SMTP settings first to test.'
          : 'SMTP test failed. Check your settings.',
      });
    } finally {
      setIsTestingSMTP(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Email Configuration</h3>
        <p className="text-sm text-gray-500">Configure SMTP settings for email notifications</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SMTP Host
          </label>
          <input
            type="text"
            value={settings.smtpHost}
            onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SMTP Port
          </label>
          <input
            type="number"
            value={settings.smtpPort}
            onChange={(e) => setSettings({ ...settings, smtpPort: parseInt(e.target.value) })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SMTP Username
          </label>
          <input
            type="text"
            value={settings.smtpUsername}
            onChange={(e) => setSettings({ ...settings, smtpUsername: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SMTP Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={settings.smtpPassword}
              onChange={(e) => setSettings({ ...settings, smtpPassword: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4 text-gray-400" />
              ) : (
                <Eye className="w-4 h-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            From Email
          </label>
          <input
            type="email"
            value={settings.fromEmail}
            onChange={(e) => setSettings({ ...settings, fromEmail: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            From Name
          </label>
          <input
            type="text"
            value={settings.fromName}
            onChange={(e) => setSettings({ ...settings, fromName: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="enableTLS"
          checked={settings.enableTLS}
          onChange={(e) => setSettings({ ...settings, enableTLS: e.target.checked })}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="enableTLS" className="ml-2 text-sm text-gray-700">
          Enable TLS/SSL encryption
        </label>
      </div>

      {testResult && (
        <div className={`p-4 rounded-md ${
          testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center">
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            )}
            <span className={`text-sm font-medium ${
              testResult.success ? 'text-green-800' : 'text-red-800'
            }`}>
              {testResult.message}
            </span>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={handleTestSMTP}
          disabled={isTestingSMTP}
          className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50 flex items-center"
        >
          <Mail className="w-4 h-4 mr-2" />
          {isTestingSMTP ? 'Testing...' : 'Test Connection'}
        </button>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-green-600 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />Saved</span>}
          <button
            onClick={handleSaveSMTP}
            disabled={isSaving}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SecuritySettings: React.FC = () => {
  const [settings, setSettings] = useState({
    mfaRequired: false,
    passwordMinLength: 8,
    passwordRequireUppercase: true,
    passwordRequireLowercase: true,
    passwordRequireNumbers: true,
    passwordRequireSymbols: true,
    passwordExpiryDays: 90,
    jwtExpiryHours: 24,
    refreshTokenExpiryDays: 30,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Security policy is stored in the same /admin/settings document as the System
  // tab; the backend PUT merges, so saving here won't clobber System fields.
  const { data: loaded } = useQuery({ queryKey: ['system-settings'], queryFn: () => adminAPI.getSystemSettings() });
  useEffect(() => {
    if (loaded) setSettings((prev) => ({ ...prev, ...loaded }));
  }, [loaded]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaved(false);
    try {
      await adminAPI.updateSystemSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Failed to save security settings', e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Security Configuration</h3>
        <p className="text-sm text-gray-500">Configure security policies and requirements</p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="mfaRequired"
            checked={settings.mfaRequired}
            onChange={(e) => setSettings({ ...settings, mfaRequired: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="mfaRequired" className="ml-2 text-sm text-gray-700">
            Require multi-factor authentication for all users
          </label>
        </div>
      </div>

      <div>
        <h4 className="text-md font-medium text-gray-900 mb-3">Password Policy</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Length
            </label>
            <input
              type="number"
              min="6"
              max="128"
              value={settings.passwordMinLength}
              onChange={(e) => setSettings({ ...settings, passwordMinLength: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password Expiry (days)
            </label>
            <input
              type="number"
              min="0"
              value={settings.passwordExpiryDays}
              onChange={(e) => setSettings({ ...settings, passwordExpiryDays: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Set to 0 for no expiry</p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="passwordRequireUppercase"
              checked={settings.passwordRequireUppercase}
              onChange={(e) => setSettings({ ...settings, passwordRequireUppercase: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="passwordRequireUppercase" className="ml-2 text-sm text-gray-700">
              Require uppercase letters
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="passwordRequireLowercase"
              checked={settings.passwordRequireLowercase}
              onChange={(e) => setSettings({ ...settings, passwordRequireLowercase: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="passwordRequireLowercase" className="ml-2 text-sm text-gray-700">
              Require lowercase letters
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="passwordRequireNumbers"
              checked={settings.passwordRequireNumbers}
              onChange={(e) => setSettings({ ...settings, passwordRequireNumbers: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="passwordRequireNumbers" className="ml-2 text-sm text-gray-700">
              Require numbers
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="passwordRequireSymbols"
              checked={settings.passwordRequireSymbols}
              onChange={(e) => setSettings({ ...settings, passwordRequireSymbols: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="passwordRequireSymbols" className="ml-2 text-sm text-gray-700">
              Require symbols (!@#$%^&*)
            </label>
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-md font-medium text-gray-900 mb-3">Token Settings</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              JWT Expiry (hours)
            </label>
            <input
              type="number"
              min="1"
              max="168"
              value={settings.jwtExpiryHours}
              onChange={(e) => setSettings({ ...settings, jwtExpiryHours: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Refresh Token Expiry (days)
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={settings.refreshTokenExpiryDays}
              onChange={(e) => setSettings({ ...settings, refreshTokenExpiryDays: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-green-600 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />Saved</span>}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

const DatabaseSettings: React.FC = () => {
  const [backupStatus, setBackupStatus] = useState<'idle' | 'running' | 'success' | 'error'>('idle');
  const queryClient = useQueryClient();

  const { data: backups = [] } = useQuery({ queryKey: ['backups'], queryFn: () => adminAPI.getBackups() });

  const handleBackup = async () => {
    setBackupStatus('running');
    try {
      await adminAPI.createBackup();
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setBackupStatus('success');
      setTimeout(() => setBackupStatus('idle'), 3000);
    } catch (error) {
      setBackupStatus('error');
      setTimeout(() => setBackupStatus('idle'), 3000);
    }
  };

  const lastBackup = backups.length
    ? new Date(Math.max(...backups.map((b: any) => b.createdAt || 0))).toLocaleString()
    : 'None yet';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Database Management</h3>
        <p className="text-sm text-gray-500">Database backup, maintenance, and monitoring</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center">
            <Database className="w-8 h-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Backups</p>
              <p className="text-lg font-semibold text-gray-900">{backups.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Last Backup</p>
              <p className="text-lg font-semibold text-gray-900">{lastBackup}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Database Backup</h4>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">
              Create a full database backup for disaster recovery
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Backups are stored securely and encrypted
            </p>
          </div>
          <button
            onClick={handleBackup}
            disabled={backupStatus === 'running'}
            className={`px-4 py-2 rounded-md flex items-center ${
              backupStatus === 'running'
                ? 'bg-gray-400 cursor-not-allowed'
                : backupStatus === 'success'
                ? 'bg-green-600 hover:bg-green-700'
                : backupStatus === 'error'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white`}
          >
            {backupStatus === 'running' && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            )}
            {backupStatus === 'success' && <CheckCircle className="w-4 h-4 mr-2" />}
            {backupStatus === 'error' && <AlertTriangle className="w-4 h-4 mr-2" />}
            {backupStatus === 'idle' && <Database className="w-4 h-4 mr-2" />}
            {backupStatus === 'running' ? 'Creating Backup...' : 
             backupStatus === 'success' ? 'Backup Created!' :
             backupStatus === 'error' ? 'Backup Failed' :
             'Create Backup'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Recent Backups</h4>
        {backups.length === 0 ? (
          <p className="text-sm text-gray-500">No backups yet. Create one above.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {backups
              .slice()
              .sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0))
              .map((b: any) => (
                <div key={b.backupArn} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{b.name}</p>
                    <p className="text-xs text-gray-500">{b.createdAt ? new Date(b.createdAt).toLocaleString() : '—'}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${b.status === 'AVAILABLE' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {b.status || 'CREATING'}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('system');

  const sections: SettingsSection[] = [
    {
      id: 'system',
      name: 'System',
      icon: <SettingsIcon className="w-5 h-5" />,
      component: SystemSettings,
    },
    {
      id: 'email',
      name: 'Email',
      icon: <Mail className="w-5 h-5" />,
      component: EmailSettings,
    },
    {
      id: 'security',
      name: 'Security',
      icon: <Shield className="w-5 h-5" />,
      component: SecuritySettings,
    },
    {
      id: 'database',
      name: 'Database',
      icon: <Database className="w-5 h-5" />,
      component: DatabaseSettings,
    },
  ];

  const ActiveComponent = sections.find(s => s.id === activeSection)?.component || SystemSettings;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage system configuration and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                  activeSection === section.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {section.icon}
                <span className="ml-3">{section.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <ActiveComponent />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
